import { useQuery } from "@tanstack/react-query";
import { unifiedSports } from "@/lib/sports/unifiedSportsService";
import type { ProviderLineup } from "@/lib/sports/providers/types";

export interface UseMatchLineupsResult {
  lineup: ProviderLineup | undefined;
  isLoading: boolean;
  isError: boolean;
  updatedAt: number | undefined;
}

/**
 * Real Data Phase 3 — official lineups (API-Football `/fixtures/lineups`).
 *
 * Never fabricates. When the provider has no data (pre-match) or fails, the
 * returned payload has `official: false` and empty `home`/`away` blocks so
 * the UI renders the lifecycle-appropriate placeholder from `dataPolicy.ts`.
 */
export function useMatchLineups(fixtureId: string | undefined): UseMatchLineupsResult {
  const enabled = typeof fixtureId === "string" && fixtureId.length > 0;
  const queryKey = ["match-lineups", fixtureId ?? "__none__"] as const;
  if (typeof window !== "undefined") {
    console.info("[TRACE][useMatchLineups][mount]", { enabled, fixtureId, queryKey });
  }
  const q = useQuery<ProviderLineup>({
    queryKey,
    queryFn: async () => {
      const t0 = Date.now();
      console.info("[TRACE][useMatchLineups][queryFn:start]", { fixtureId, at: new Date().toISOString() });
      try {
        const r = await unifiedSports.getLineups(fixtureId as string);
        console.info("[TRACE][useMatchLineups][queryFn:success]", {
          fixtureId, ms: Date.now() - t0,
          official: r?.official, homeXI: r?.home?.startXI?.length ?? 0, awayXI: r?.away?.startXI?.length ?? 0,
        });
        return r;
      } catch (e) {
        console.warn("[TRACE][useMatchLineups][queryFn:error]", { fixtureId, err: String(e) });
        throw e;
      }
    },
    enabled,
    refetchInterval: 60_000,
    staleTime: 45_000,
    retry: 1,
  });
  if (typeof window !== "undefined") {
    console.info("[TRACE][useMatchLineups][state]", {
      fixtureId, enabled, status: q.status, fetchStatus: q.fetchStatus,
      dataUpdatedAt: q.dataUpdatedAt, isFetching: q.isFetching,
    });
  }
  return {
    lineup: q.data,
    isLoading: enabled && q.isLoading,
    isError: q.isError,
    updatedAt: q.dataUpdatedAt || undefined,
  };
}